import {
  CircleLoader,
  EmptyState,
  Header,
  IconSearch,
  Pagination,
  Space,
  TextField,
} from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { type SecondaryCategoryConfig } from '@webapp/platform/organization-management/entities/SecondaryCategoryConfig'
import messages from './messages'
import { ListLoadingWrapperStyled } from './styles/ListLoadingWrapperStyled'
import { SecondaryCategoryRowStyled } from './styles/SecondaryCategoryRowStyled'
import { SelectAllBarStyled } from './styles/SelectAllBarStyled'

export interface IReportAccessSecondaryCategoryListViewProps {
  primaryCategoryName: string
  secondaryCategoryConfigs: SecondaryCategoryConfig[]
  selectedSecondaryCategoryId: number | undefined
  isAllSecondaryCategoriesSelected: boolean
  isSelectAllBarVisible: boolean
  searchTerm: string
  onSearchChange: (term: string) => void
  currentPage: number
  numPages: number
  onPageChange: (page: number) => void
  isLoading: boolean
  onRowClick: (config: SecondaryCategoryConfig) => void
  onToggleSelectAll: () => void
}

export const ReportAccessSecondaryCategoryListView = ({
  primaryCategoryName,
  secondaryCategoryConfigs,
  selectedSecondaryCategoryId,
  isAllSecondaryCategoriesSelected,
  isSelectAllBarVisible,
  searchTerm,
  onSearchChange,
  currentPage,
  numPages,
  onPageChange,
  isLoading,
  onRowClick,
  onToggleSelectAll,
}: IReportAccessSecondaryCategoryListViewProps) => {
  const intl = useProcurifyIntl()

  const isEmpty = secondaryCategoryConfigs.length === 0
  const isSearching = searchTerm.trim().length > 0

  return (
    <Space>
      <Header as='h5'>
        {intl.formatMessage(messages.heading, { primaryCategoryName })}
      </Header>

      <TextField
        fullWidth
        placeholder={intl.formatMessage(messages.searchPlaceholder)}
        value={searchTerm}
        onChange={(event) => onSearchChange(event.target.value)}
        InputProps={{
          endAdornment: <IconSearch size={16} />,
        }}
        inputProps={{
          'aria-label': intl.formatMessage(messages.searchAriaLabel),
        }}
      />

      {isEmpty && isLoading ? (
        <CircleLoader />
      ) : isEmpty ? (
        <EmptyState
          small
          header={intl.formatMessage(messages.emptyStateHeader)}
          content={
            isSearching
              ? undefined
              : intl.formatMessage(messages.emptyStateContent)
          }
        />
      ) : (
        <>
          <ListLoadingWrapperStyled $isLoading={isLoading}>
            {isSelectAllBarVisible && (
              <SelectAllBarStyled type='button' onClick={onToggleSelectAll}>
                {isAllSecondaryCategoriesSelected
                  ? intl.formatMessage(messages.allSelectedLabel)
                  : intl.formatMessage(messages.selectAllLabel)}
              </SelectAllBarStyled>
            )}

            {secondaryCategoryConfigs.map((config) => (
              <SecondaryCategoryRowStyled
                key={config.id}
                type='button'
                $isActive={
                  !isAllSecondaryCategoriesSelected &&
                  config.id === selectedSecondaryCategoryId
                }
                $isDimmed={isAllSecondaryCategoriesSelected}
                onClick={() => onRowClick(config)}
              >
                {config.name}
              </SecondaryCategoryRowStyled>
            ))}
          </ListLoadingWrapperStyled>

          <Space direction='horizontal' justify='center'>
            <Pagination
              currentPage={currentPage}
              numPages={numPages}
              onPageChange={(page) => onPageChange(page)}
            />
          </Space>
        </>
      )}
    </Space>
  )
}

ReportAccessSecondaryCategoryListView.displayName =
  'ReportAccessSecondaryCategoryListView'
